import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function SmartTreasuryPage() {
  const { t } = useTranslation();

  const benefits = [
    { icon: 'water_drop', title: t('smartTreasuryPage.benefits.liquidity.title'), desc: t('smartTreasuryPage.benefits.liquidity.desc') },
    { icon: 'check_box', title: t('smartTreasuryPage.benefits.control.title'), desc: t('smartTreasuryPage.benefits.control.desc') },
    { icon: 'currency_exchange', title: t('smartTreasuryPage.benefits.fx.title'), desc: t('smartTreasuryPage.benefits.fx.desc') },
    { icon: 'bolt', title: t('smartTreasuryPage.benefits.agility.title'), desc: t('smartTreasuryPage.benefits.agility.desc') },
  ];

  useEffect(() => {
    document.title = t('smartTreasuryPage.pageTitle');
  }, [t]);

  return (
    <main id="main-content">
      <section className="pt-40 pb-20 text-center relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center_top,rgba(168,67,151,0.08),transparent_70%)] pointer-events-none" />
        <div className="mx-auto min-w-0 max-w-4xl px-4 sm:px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black font-headline tracking-tight">
              {t('smartTreasuryPage.heroTitle')} <span className="gradient-text">{t('smartTreasuryPage.heroTitleHighlight')}</span>
            </h1>
            <p className="text-on-surface-variant text-base sm:text-lg mt-4 max-w-xl mx-auto">
              {t('smartTreasuryPage.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-surface-container/20">
        <div className="mx-auto min-w-0 max-w-4xl px-4 sm:px-6 text-center">
          <h2 className="text-3xl font-black font-headline mb-6">{t('smartTreasuryPage.whatIs.title')}</h2>
          <p className="text-on-surface-variant leading-relaxed text-lg">
            {t('smartTreasuryPage.whatIs.text')}
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto min-w-0 max-w-7xl px-4 sm:px-6">
          <h2 className="text-3xl font-black font-headline text-center mb-12">{t('smartTreasuryPage.benefits.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                className="text-center p-8 border border-outline-variant/30 rounded-xl hover:border-purple-primary/50 hover:shadow-[0_0_20px_rgba(168,67,151,0.1)] transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="w-14 h-14 rounded-lg bg-purple-primary/10 border border-purple-primary/30 flex items-center justify-center mx-auto mb-5">
                  <span className="material-symbols-outlined text-purple-primary text-[28px]">{b.icon}</span>
                </div>
                <h3 className="text-lg font-bold font-headline mb-2">{b.title}</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto min-w-0 max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between p-5 sm:p-8 bg-surface-container border border-outline-variant/30 rounded-2xl">
            <h3 className="text-xl md:text-2xl font-bold font-headline mb-4 md:mb-0">
              {t('smartTreasuryPage.cta.title')} <span className="gradient-text">{t('smartTreasuryPage.cta.titleHighlight')}</span>
            </h3>
            <Link to="/contact" className="px-8 py-3 bg-gradient-to-r from-purple-primary to-accent-magenta text-white rounded-xl font-bold font-headline hover:scale-105 active:scale-95 transition-transform">
              {t('smartTreasuryPage.cta.button')}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
